import React, { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import '../legal/legal.css';
import Header from './Header';
import Footer from './Footer';

// ─────────────────────────────────────────────────────────────────────────────
// SiteLayout — the chrome wrapper for the public website (Header + page + Footer).
// Used as a React Router *layout route* so the screens themselves stay free of
// header/footer markup:
//
//   <Route element={<SiteLayout />}>
//     <Route path="/hilfe" element={<HelpCenter />} />
//     ...
//   </Route>
//
// When a page is opened inside an in-app WebView, the app appends `?webview=1`
// (or `?embed=1`) to the URL and the chrome is omitted — only the page content
// renders. The flag is remembered for the session so in-app navigation keeps the
// bare layout.
// ─────────────────────────────────────────────────────────────────────────────

const EMBED_KEY = 'me.embed';
// /model is the full-screen seat map designer — it owns the whole viewport, so
// the website header/footer never render there.
const CHROMELESS_ROUTES = new Set(['/checkout', '/selection', '/venue', '/model']);

function useIsEmbedded(): boolean {
    const { pathname, search } = useLocation();
    const params = new URLSearchParams(search);
    const chromelessRoute = CHROMELESS_ROUTES.has(pathname);
    const flagged =
        params.get('webview') === '1' ||
        params.get('embed') === '1' ||
        params.get('mode') === 'app';

    useEffect(() => {
        if (flagged) {
            try {
                sessionStorage.setItem(EMBED_KEY, '1');
            } catch {
                /* storage unavailable — fall back to the URL flag only */
            }
        }
    }, [flagged]);

    let remembered = false;
    try {
        remembered = sessionStorage.getItem(EMBED_KEY) === '1';
    } catch {
        /* ignore */
    }
    return chromelessRoute || flagged || remembered;
}

export default function SiteLayout() {
    const embedded = useIsEmbedded();

    return (
        <div className="se-page">
            {!embedded && <Header />}
            <Outlet />
            {!embedded && <Footer />}
        </div>
    );
}
